import React, { useContext, useEffect, useMemo } from 'react'
import Steps from '@components/Steps/Steps'
import RegistrationContext from '@contexts/Registration'
import { useNavigate } from 'react-router'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import * as $EntityRegistration from '@services/EntityRegistration'
import Selection from '@components/Registration/Selection/Selection'
import Finish from '@components/Registration/Finish/Finish'
import Upload from '@components/Registration/RegistrationTypes/Upload/Upload'
import Manual from '@components/Registration/RegistrationTypes/Manual/Manual'
import CopyPaste from '@components/Registration/RegistrationTypes/CopyPaste/CopyPaste'
import Degrees from '@components/Registration/Degrees/Degrees'
import RoleEnum from '@enums/Role.enum'
import RegistrationStepEnum from '@enums/RegistrationStep.enum'
import RegistrationTypeEnum from '@enums/RegistrationType.enum'
import Migration from '@components/Registration/RegistrationTypes/Migration/Migration'

const StudentsRegister: React.FC<any> = () => {
  const { title, token, registrationType, step, setStep, setRole, setEntityLabels, clear, isLoading, setIsLoading } = useContext(RegistrationContext)

  const SweetAlert = withReactContent(Swal)
  const navigate = useNavigate()

  useEffect(() => {
    setRole(RoleEnum.Student)

    setEntityLabels({
      plural: 'Alunos',
      singular: 'Aluno',
    })
  }, [setEntityLabels, setRole])

  const handleStepClick = (step: number) => {
    switch (step) {
    case 1:
      setStep(RegistrationStepEnum.Degrees)
      break

    case 2:
      setStep(RegistrationStepEnum.Selection)
      break

    case 3:
      setStep(RegistrationStepEnum.Data)
      break

    case 4:
      setStep(RegistrationStepEnum.Finish)
      break
    }
  }

  const currentStep = useMemo((): number => {
    switch (step) {
    case RegistrationStepEnum.Degrees:
      return 1
    case RegistrationStepEnum.Selection:
      return 2
    case RegistrationStepEnum.Data:
      return 3
    case RegistrationStepEnum.Finish:
      return 4
    }

    return 1
  }, [step])

  const onFinish = () => {
    setIsLoading(true)

    $EntityRegistration.finish(token).then(() => {
      clear()

      navigate('/alunos')

      SweetAlert.fire({
        title: 'Sucesso!',
        text: 'Alunos cadastrados com sucesso!',
        icon: 'success',
        allowEscapeKey: false,
        allowOutsideClick: false,
        showConfirmButton: true,
      })
    }).catch(() => {
      SweetAlert.fire({
        title: 'Erro!',
        text: 'Erro ao cadastrar os alunos!',
        icon: 'error'
      })
    }).finally(() => setIsLoading(false))
  }

  return (
    <>
      <h1 className="fs-3 mb-3">Cadastro de alunos</h1>

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="mb-0">{title}</span>
          <Steps
            current={currentStep}
            range={[ 1, 4 ]}
            onClick={handleStepClick}
            disabled={isLoading}
          />
        </div>

        {step === RegistrationStepEnum.Degrees && <Degrees />}

        {step === RegistrationStepEnum.Selection && (
          <Selection
            uses={[
              'upload', 'manual', 'copyPaste', 'migration',
            ]}
            instructions={{
              upload: (
                <>Inscreva os alunos via upload de planilha de Excel. Caso selecionado este modelo, o modelo de planilha e forma de preenchimento serão mostrados na próxima etapa.</>
              ),
              copyPaste: (
                <>Copie e cole os dados dos alunos de um arquivo salvo atentando-se aos dados informados e espaçamentos. Caso selecionado este formato, o modelo de preenchimento será mostrado na etapa a seguir.</>
              ),
              manual: (
                <>Preencha manualmente um formulário na plataforma com as informações dos alunos. Caso selecionado este formato, o modelo de preenchimento será exibido na etapa a seguir.</>
              ),
              migration: (
                <>Selecione alunos de anos anteriores e monte novas turmas mantendo todas as informações existentes no cadastro de cada um.</>
              )
            }}
          />
        )}

        {step === RegistrationStepEnum.Data && registrationType === RegistrationTypeEnum.Upload && (
          <Upload
            instruction={(
              <>
                Preencher a planilha com o identificador numérico exclusivo de cada estudante (Registro Acadêmico ou Registro de Matrícula), nome completo do estudante, turma (cadastrada anteriormente) e série do aluno . Os campos de senha e e-mail são opcionais.
                <br/>
                Caso a escola não possua identificador único por aluno, entrar em contato com o atendimento ao cliente.
                <br/>
                Atenção: dois alunos diferentes não podem possuir o mesmo RA, mesmo estando em turmas ou séries diferentes.
              </>
            )}
          />
        )}

        {step === RegistrationStepEnum.Data && registrationType === RegistrationTypeEnum.Manual && (
          <Manual
            instruction={(
              <>
                Preencher dados com o identificador numérico exclusivo de cada estudante (Registro Acadêmico ou Registro de Matrícula), nome completo do estudante, turma (cadastrada anteriormente) e série do aluno e, em seguida, copiar tabela e colar na área lateral. Os campos de senha e e-mail são opcionais.
                <br/>
                Caso a escola não possua identificador único por aluno, entrar em contato com o atendimento ao cliente.
                <br/>
                Atenção: dois alunos diferentes não podem possuir o mesmo RA, mesmo estando em turmas ou séries diferentes.
              </>
            )}
          />
        )}

        {step === RegistrationStepEnum.Data && registrationType === RegistrationTypeEnum.CopyPaste && (
          <CopyPaste
            instruction={(
              <>
                Preencher uma planilha com o identificador numérico exclusivo de cada estudante (Registro Acadêmico ou Registro de Matrícula), nome completo do estudante, turma (cadastrada anteriormente) e série do aluno e, em seguida, copiar tabela e colar na área lateral. Os campos de senha e e-mail são opcionais.
                <br/>
                Caso a escola não possua identificador único por aluno, entrar em contato com o atendimento ao cliente.
                <br/>
                Atenção: dois alunos diferentes não podem possuir o mesmo RA, mesmo estando em turmas ou séries diferentes.
              </>
            )}
          />
        )}

        {step === RegistrationStepEnum.Data && registrationType === RegistrationTypeEnum.Migration && (
          <Migration />
        )}

        {step === RegistrationStepEnum.Finish && (
          <Finish onFinish={onFinish} isLoading={isLoading} />
        )}
      </div>
    </>
  )
}

export default StudentsRegister
